// src/components/team/Invites.js
// ✅ DROP-IN: side-by-side layout
//   - Left (1/3): Invite form
//   - Right (2/3): All users list in grouped dropdowns
// ✅ Deep-link works because TeamScreen calls onAccountSelect(accountId)
// ✅ Normal navigation: fields remain blank until user selects an account
// ✅ New Account option: "__new__" shows basic account fields and TeamScreen creates the account before invite
// ✅ If email already has a customer user or pending invite, show status + block sending

import React, { useCallback, useMemo, useState } from "react";
import { 
  AlertCircle, Loader2, Mail, X, RefreshCw, CheckCircle2, Clock,
  ChevronDown, ChevronRight, UserCheck, UserX, Users, Building, ArrowUpRight
} from "lucide-react";

const normalizeEmail = (email) => (email || "").toString().trim().toLowerCase();
const normalizeRole = (role) => {
  const r = (role || "").toString().trim().toLowerCase();
  if (["enduser", "end user", "end_user", "end-user"].includes(r)) return "enduser";
  if (r === "admin") return "admin";
  if (r === "owner") return "owner";
  if (r === "customer") return "customer";
  return r;
};

const prettyRole = (role) => {
  const r = normalizeRole(role);
  if (r === "admin") return "Admin";
  if (r === "enduser") return "Team";
  if (r === "customer") return "Customer";
  if (r === "owner") return "Owner";
  return role || "—";
};

const formatDate = (ts) => {
  try {
    if (!ts) return "—";
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : ts?.seconds
        ? new Date(ts.seconds * 1000)
        : new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
};

const formatLastSeen = (ts) => {
  try {
    if (!ts) return "Last seen unknown";
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : ts?.seconds
        ? new Date(ts.seconds * 1000)
        : new Date(ts);
    if (Number.isNaN(d.getTime())) return "Last seen unknown";

    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.max(0, Math.floor(diffMs / 60000));
    if (diffMin < 1) return "Last seen just now";
    if (diffMin < 60) return `Last seen ${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `Last seen ${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `Last seen ${diffDay}d ago`;
    return `Last seen ${d.toLocaleDateString()}`;
  } catch {
    return "Last seen unknown";
  }
};

const toDateValue = (ts) => {
  if (!ts) return null;
  const d =
    typeof ts?.toDate === "function"
      ? ts.toDate()
      : ts?.seconds
      ? new Date(ts.seconds * 1000)
      : ts instanceof Date
      ? ts
      : new Date(ts);
  return Number.isNaN(d?.getTime?.()) ? null : d;
};

const isRecentOnlinePresence = (item) => {
  const status = String(item?.presenceStatus || "").trim().toLowerCase();
  if (status !== "online") return false;

  const presenceUpdatedAt = toDateValue(item?.presenceUpdatedAt) || toDateValue(item?.updatedAt);
  if (!presenceUpdatedAt) return false;

  return Date.now() - presenceUpdatedAt.getTime() <= 5 * 60 * 1000;
};

const formatPresenceActivity = (item) => {
  const presenceUpdatedAt = toDateValue(item?.presenceUpdatedAt) || toDateValue(item?.updatedAt);
  if (!presenceUpdatedAt) return formatLastSeen(item?.lastSeen);

  const diffMs = Date.now() - presenceUpdatedAt.getTime();
  const diffMin = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMin < 1) return "Active just now";
  if (diffMin < 60) return `Active ${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `Active ${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `Active ${diffDay}d ago`;
  return `Active ${presenceUpdatedAt.toLocaleDateString()}`;
};

const renderPresenceBadge = (item, groupKey) => {
  if (groupKey === "pending") return null;

  if (isRecentOnlinePresence(item)) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Online now
      </span>
    );
  }

  if (String(item?.presenceStatus || "").trim().toLowerCase() === "online") {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-blue-200">
        {formatPresenceActivity(item)}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
      {formatLastSeen(item?.lastSeen)}
    </span>
  );
};

const inputBase =
  "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
const labelBase = "text-xs font-semibold text-slate-700";

const ACCOUNT_TYPES = [
  "Restaurant",
  "Convenience Store",
  "School",
  "Hotel",
  "Healthcare",
  "Office",
  "Other",
];

export default function Invites({
  companyId,
  currentUserDoc,
  company,
  inviteForm,
  setInviteForm,
  handleInviteUser,
  inviting,
  pendingInvites,
  busyInviteId,
  onCancelInvite,
  onResendInvite,
  accounts,
  error,
  onAccountSelect,
  onOpenSelectedAccount,
  isAdminView,
  canInviteTeamMembers = false,
  canInviteAdmins = false,
  canManageRoleChanges = false,
  canDeactivateUsers = false,
  seats,

  checkingCustomerEmail,
  existingCustomerUserByEmail,
  pendingCustomerInviteByEmail,
  
  // NEW PROPS for all user groups
  activeUsers = [],
  inactiveUsers = [],
  customers = [],
  userAssignmentStats = {},
  onDeactivateUser,
  onReactivateUser,
  onRemoveCustomerAccess,
  onChangeStaffRole,
}) {
  const [search, setSearch] = useState("");
  const [roleActionBusyId, setRoleActionBusyId] = useState("");
  const [roleActionError, setRoleActionError] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({
    pending: false,
    active: true,
    inactive: false,
    customers: true,
  });

  const roleNorm = normalizeRole(inviteForm?.role);
  const isCustomerInvite = roleNorm === "customer";
  const isNewAccount = isCustomerInvite && inviteForm?.accountId === "__new__";

  const selectedAccount = useMemo(() => {
    if (!isCustomerInvite) return null;
    const id = (inviteForm?.accountId || "").toString().trim();
    if (!id || id === "__new__") return null;
    return (accounts || []).find((a) => a.id === id) || null;
  }, [accounts, inviteForm?.accountId, isCustomerInvite]);

  const accountInviteState = useMemo(() => {
    const pendingByAccount = new Map();
    const activeByAccount = new Map();

    (pendingInvites || []).forEach((invite) => {
      const accountId = (invite?.accountId || "").toString().trim();
      if (!accountId) return;
      pendingByAccount.set(accountId, (pendingByAccount.get(accountId) || 0) + 1);
    });

    (customers || []).forEach((customer) => {
      const accountIds = []
        .concat(Array.isArray(customer?.accountIds) ? customer.accountIds : [])
        .concat(customer?.accountId || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      Array.from(new Set(accountIds)).forEach((accountId) => {
        activeByAccount.set(accountId, (activeByAccount.get(accountId) || 0) + 1);
      });
    });

    return { pendingByAccount, activeByAccount };
  }, [customers, pendingInvites]);

  const selectedAccountInviteMeta = useMemo(() => {
    if (!selectedAccount?.id) return null;
    const activeCount = accountInviteState.activeByAccount.get(selectedAccount.id) || 0;
    const pendingCount = accountInviteState.pendingByAccount.get(selectedAccount.id) || 0;
    if (!activeCount && !pendingCount) return null;

    return {
      activeCount,
      pendingCount,
      tone:
        activeCount > 0
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-amber-200 bg-amber-50 text-amber-800",
      label:
        activeCount > 0
          ? `${activeCount} portal ${activeCount === 1 ? "user" : "users"} active`
          : `${pendingCount} invite${pendingCount === 1 ? "" : "s"} pending`,
    };
  }, [accountInviteState.activeByAccount, accountInviteState.pendingByAccount, selectedAccount]);

  const isExistingAccountSelected = Boolean(selectedAccount && isCustomerInvite && !isNewAccount);

  const customerAccountSummaryByKey = useMemo(() => {
    const summary = new Map();

    (customers || []).forEach((customer) => {
      const identityKey =
        normalizeEmail(customer?.email) ||
        String(customer?.id || customer?.uid || "").trim() ||
        String(customer?.accountId || "").trim();
      if (!identityKey) return;

      const names = new Set(summary.get(identityKey) || []);
      const directName = String(customer?.accountName || customer?.businessName || "").trim();
      if (directName) names.add(directName);

      (Array.isArray(customer?.accountNames) ? customer.accountNames : []).forEach((name) => {
        const clean = String(name || "").trim();
        if (clean) names.add(clean);
      });

      (Array.isArray(customer?.accounts) ? customer.accounts : []).forEach((account) => {
        const clean = String(account?.name || account?.accountName || account || "").trim();
        if (clean) names.add(clean);
      });

      summary.set(identityKey, Array.from(names));
    });

    return summary;
  }, [customers]);

  const getCustomerAccountLabel = useCallback(
    (customer) => {
      const identityKey =
        normalizeEmail(customer?.email) ||
        String(customer?.id || customer?.uid || "").trim() ||
        String(customer?.accountId || "").trim();
      const accountNames = customerAccountSummaryByKey.get(identityKey) || [];
      const primaryName =
        String(customer?.accountName || customer?.businessName || accountNames[0] || "").trim();
      if (!primaryName) return "";
      if (accountNames.length <= 1) return primaryName;
      return `${primaryName} +${accountNames.length - 1}`;
    },
    [customerAccountSummaryByKey]
  );

  // Filter all user groups by search
  const filterUsers = useCallback((users) => {
    const q = (search || "").toLowerCase().trim();
    if (!q) return users;
    
    return users.filter((user) => {
      const email = (user.email || "").toLowerCase();
      const name = (user.fullName || user.name || user.contactName || "").toLowerCase();
      const role = (user.role || "").toLowerCase();
      const account = (user.accountName || user.businessName || "").toLowerCase();
      const additionalAccounts = (Array.isArray(user.accountNames) ? user.accountNames : [])
        .map((value) => String(value || "").toLowerCase())
        .join(" ");
      
      return email.includes(q) || 
             name.includes(q) || 
             role.includes(q) || 
             account.includes(q) ||
             additionalAccounts.includes(q);
    });
  }, [search]);

  const filteredPending = useMemo(() => filterUsers(pendingInvites || []), [pendingInvites, filterUsers]);
  const filteredActive = useMemo(() => filterUsers(activeUsers || []), [activeUsers, filterUsers]);
  const filteredInactive = useMemo(() => filterUsers(inactiveUsers || []), [inactiveUsers, filterUsers]);
  const filteredCustomers = useMemo(() => filterUsers(customers || []), [customers, filterUsers]);

  const seatBlockedReason = useMemo(() => {
    if (!isAdminView) return "";
    if (roleNorm === "admin" && (seats?.adminSeatsLeft ?? 0) <= 0) return "No admin seats left.";
    if (roleNorm === "enduser" && (seats?.endUserSeatsLeft ?? 0) <= 0) return "No team seats left.";
    return "";
  }, [isAdminView, roleNorm, seats]);

  const customerEmailBlockedReason = useMemo(() => {
    if (!isCustomerInvite) return "";
    const email = normalizeEmail(inviteForm?.email);
    if (!email) return "";
    if (existingCustomerUserByEmail) return "That email already has portal access.";
    if (pendingCustomerInviteByEmail) return "That email already has a pending invite.";
    return "";
  }, [inviteForm?.email, existingCustomerUserByEmail, pendingCustomerInviteByEmail, isCustomerInvite]);

  const canSubmit =
    !inviting &&
    !seatBlockedReason &&
    !customerEmailBlockedReason;

  const setField = (k, v) => setInviteForm((prev) => ({ ...(prev || {}), [k]: v }));

  const handleAccountDropdownChange = (val) => {
    if (!val) {
      // blank
      setInviteForm((prev) => ({
        ...(prev || {}),
        accountId: "",
        accountName: "",
        contactName: "",
        email: "",
      }));
      return;
    }

    if (val === "__new__") {
      // new account mode
      setInviteForm((prev) => ({
        ...(prev || {}),
        role: "customer",
        accountId: "__new__",
        accountName: "",
        contactName: "",
        email: "",
        accountType: prev?.accountType || "Restaurant",
        accountNumber: prev?.accountNumber || "",
        accountPhone: prev?.accountPhone || "",
        addressStreet: prev?.addressStreet || "",
        addressStreet2: prev?.addressStreet2 || "",
        addressCity: prev?.addressCity || "",
        addressState: prev?.addressState || "",
        addressZip: prev?.addressZip || "",
      }));
      return;
    }

    // existing account select (TeamScreen fills name/email too)
    onAccountSelect?.(val);
  };

  const showCustomerPrefillHint = !!selectedAccount && isCustomerInvite && !isNewAccount;

  // Helper to get role badge styling
  const getRoleBadge = (role, groupKey) => {
    const roleNorm = normalizeRole(role);
    if (groupKey === "customers" || roleNorm === "customer") return null;
    const config = {
      admin: { label: "Admin", className: "bg-purple-100 text-purple-800 border-purple-200" },
      owner: { label: "Owner", className: "bg-amber-100 text-amber-800 border-amber-200" },
      enduser: { label: "Team", className: "bg-blue-100 text-blue-800 border-blue-200" },
    };
    
    const cfg = config[roleNorm] || { label: prettyRole(role), className: "bg-slate-100 text-slate-800 border-slate-200" };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold border ${cfg.className}`}>
        {cfg.label}
      </span>
    );
  };

  // Toggle group expansion
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // User groups configuration
  const userGroups = [
    {
      key: "pending",
      title: isAdminView ? "Pending Invites" : "My Pending Customer Invites",
      icon: <Clock className="w-4 h-4" />,
      items: filteredPending,
      emptyText: "No pending invites",
      color: "text-amber-600 bg-amber-50 border-amber-200",
      showCount: true,
    },
    ...(isAdminView
      ? [
          {
            key: "active",
            title: "Active Team Members",
            icon: <UserCheck className="w-4 h-4" />,
            items: filteredActive,
            emptyText: "No active team members",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
            showCount: true,
          },
          {
            key: "inactive",
            title: "Inactive Team Members",
            icon: <UserX className="w-4 h-4" />,
            items: filteredInactive,
            emptyText: "No inactive team members",
            color: "text-slate-600 bg-slate-50 border-slate-200",
            showCount: true,
          },
        ]
      : []),
    {
      key: "customers",
      title: isAdminView ? "Customer Portal Users" : "My Customer Portal Users",
      icon: <Users className="w-4 h-4" />,
      items: filteredCustomers,
      emptyText: "No customer portal users",
      color: "text-blue-600 bg-blue-50 border-blue-200",
      showCount: true,
    },
  ];

  // Action buttons for each group
  const getActionButtons = (user, groupKey) => {
    if (!isAdminView && (groupKey === "pending" || groupKey === "customers")) {
      return null;
    }

    const roleNorm = normalizeRole(user?.role);
    const actionUserId = user?.id || user?.uid || "";
    const isRoleActionBusy = roleActionBusyId === actionUserId;
    const adminSeatBlocked = roleNorm === "enduser" && (seats?.adminSeatsLeft ?? 0) <= 0;
    const endUserSeatBlocked = roleNorm === "admin" && (seats?.endUserSeatsLeft ?? 0) <= 0;

    const handleRoleSwap = async (targetRole) => {
      if (!onChangeStaffRole || !actionUserId) return;
      setRoleActionError("");
      setRoleActionBusyId(actionUserId);
      try {
        await onChangeStaffRole(user, targetRole);
      } catch (err) {
        setRoleActionError(err?.message || "Could not update user role.");
      } finally {
        setRoleActionBusyId("");
      }
    };

    if (groupKey === "pending") {
      const pendingRoleNorm = normalizeRole(user?.role);
      if (pendingRoleNorm === "enduser" && !canInviteTeamMembers) return null;
      if (pendingRoleNorm === "admin" && !canInviteAdmins) return null;
      const isBusy = busyInviteId === user.id;
      return (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onResendInvite?.(user)}
            disabled={isBusy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50"
          >
            {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Resend
          </button>
          <button
            type="button"
            onClick={() => onCancelInvite?.(user.id)}
            disabled={isBusy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold hover:bg-rose-100"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      );
    }
    
    if (groupKey === "active") {
      return (
        <div className="flex gap-2">
          {roleNorm === "enduser" && canManageRoleChanges ? (
            <button
              type="button"
              onClick={() => handleRoleSwap("admin")}
              disabled={isRoleActionBusy || adminSeatBlocked}
              title={adminSeatBlocked ? "No admin seats left." : "Promote to Admin"}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRoleActionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
              Make Admin
            </button>
          ) : null}
          {roleNorm === "admin" && canManageRoleChanges ? (
            <button
              type="button"
              onClick={() => handleRoleSwap("endUser")}
              disabled={isRoleActionBusy || endUserSeatBlocked}
              title={endUserSeatBlocked ? "No team seats left." : "Convert to End User"}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRoleActionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
              Make Team
            </button>
          ) : null}
          {user.role !== "owner" && canDeactivateUsers && (
            <button
              type="button"
              onClick={() => onDeactivateUser?.(user)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold hover:bg-amber-100"
            >
              Deactivate
            </button>
          )}
        </div>
      );
    }
    
    if (groupKey === "inactive") {
      return (
        <div className="flex gap-2">
          {roleNorm === "enduser" && canManageRoleChanges ? (
            <button
              type="button"
              onClick={() => handleRoleSwap("admin")}
              disabled={isRoleActionBusy || adminSeatBlocked}
              title={adminSeatBlocked ? "No admin seats left." : "Promote to Admin"}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRoleActionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
              Make Admin
            </button>
          ) : null}
          {roleNorm === "admin" && canManageRoleChanges ? (
            <button
              type="button"
              onClick={() => handleRoleSwap("endUser")}
              disabled={isRoleActionBusy || endUserSeatBlocked}
              title={endUserSeatBlocked ? "No team seats left." : "Convert to End User"}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRoleActionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
              Make Team
            </button>
          ) : null}
          {canDeactivateUsers ? (
            <button
              type="button"
              onClick={() => onReactivateUser?.(user)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100"
            >
              Reactivate
            </button>
          ) : null}
        </div>
      );
    }
    
    if (groupKey === "customers") {
      return (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onRemoveCustomerAccess?.(user)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold hover:bg-rose-100"
          >
            Remove Access
          </button>
        </div>
      );
    }
    
    return null;
  };

  const getAssignmentStats = (user) => {
    const id = user?.id || user?.uid || null;
    if (!id) return { accountsAssigned: 0, sampleOrdersAssigned: 0 };
    return userAssignmentStats?.[id] || { accountsAssigned: 0, sampleOrdersAssigned: 0 };
  };

  return (
    <div className="w-full">
      {/* 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT (1/3): Invite form - KEEP EXISTING CODE */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-slate-900 truncate">Send invite</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Invite team members or portal customers.
                </p>
              </div>
            </div>

            {/* Role */}
            <div className="space-y-1 mb-3">
              <div className={labelBase}>Role</div>
              <select
                className={inputBase}
                value={inviteForm?.role || "customer"}
                onChange={(e) => {
                  const nextRole = e.target.value;
                  // switch role: clear customer account fields when leaving customer
                  if (normalizeRole(nextRole) !== "customer") {
                    setInviteForm((prev) => ({
                      ...(prev || {}),
                      role: nextRole,
                      accountId: "",
                      accountName: "",
                    }));
                  } else {
                    setField("role", nextRole);
                  }
                }}
                disabled={!isAdminView} // end users only invite customers
              >
                {isAdminView ? (
                  <>
                    <option value="customer">Customer (Portal)</option>
                    {canInviteTeamMembers ? <option value="endUser">Team Member</option> : null}
                    {canInviteAdmins ? <option value="admin">Admin</option> : null}
                  </>
                ) : (
                  <option value="customer">Customer (Portal)</option>
                )}
              </select>

              {seatBlockedReason ? (
                <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <div>{seatBlockedReason}</div>
                </div>
              ) : null}

              {isAdminView && (!canInviteTeamMembers || !canInviteAdmins) ? (
                <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2">
                  {!canInviteTeamMembers
                    ? "You can only invite customers right now."
                    : !canInviteAdmins
                    ? "Only the owner can invite admins right now. You can still invite team members and customers."
                    : ""}
                </div>
              ) : null}
            </div>

            {/* Customer-only: Account dropdown */}
            {isCustomerInvite ? (
              <div className="space-y-1 mb-3">
                <div className={labelBase}>Account</div>
                <select
                  className={inputBase}
                  value={inviteForm?.accountId || ""}
                  onChange={(e) => handleAccountDropdownChange(e.target.value)}
                >
                  <option value="">Select an account…</option>
                  <option value="__new__">➕ New account…</option>
                  {(accounts || []).map((acc) => {
                    const name = acc.name || acc.accountName || acc.id;
                    const activeCount = accountInviteState.activeByAccount.get(acc.id) || 0;
                    const pendingCount = accountInviteState.pendingByAccount.get(acc.id) || 0;
                    const suffix =
                      activeCount > 0
                        ? ` • ${activeCount} active`
                        : pendingCount > 0
                        ? ` • ${pendingCount} pending`
                        : "";
                    return (
                      <option key={acc.id} value={acc.id}>
                        {name}{suffix}
                      </option>
                    );
                  })}
                </select>

                {selectedAccountInviteMeta ? (
                  <div className={`mt-2 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[11px] font-medium ${selectedAccountInviteMeta.tone}`}>
                    {selectedAccountInviteMeta.activeCount > 0 ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <Clock className="h-3.5 w-3.5" />
                    )}
                    <span>{selectedAccountInviteMeta.label}</span>
                  </div>
                ) : null}

                {showCustomerPrefillHint ? (
                  <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
                    <div>
                      Using the saved contact on{" "}
                      <span className="font-semibold text-slate-900">
                        {selectedAccount?.name || selectedAccount?.id}
                      </span>
                      .
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span>If you want to change contact name or email, edit the account.</span>
                      <button
                        type="button"
                        onClick={() => onOpenSelectedAccount?.(selectedAccount?.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        <ArrowUpRight className="h-3.5 w-3.5" />
                        Edit account
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* New Account fields */}
            {isNewAccount ? (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-900">New account</div>
                </div>

                <div className="space-y-2">
                  <div>
                    <div className={labelBase}>Account Type</div>
                    <select
                      className={inputBase}
                      value={inviteForm?.accountType || "Restaurant"}
                      onChange={(e) => setField("accountType", e.target.value)}
                    >
                      {ACCOUNT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className={labelBase}>
                      Account Name <span className="text-red-500">*</span>
                    </div>
                    <input
                      className={inputBase}
                      value={inviteForm?.accountName || ""}
                      onChange={(e) => setField("accountName", e.target.value)}
                      placeholder="Boston Dockside Grill"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <div className={labelBase}>Account Number</div>
                      <input
                        className={inputBase}
                        value={inviteForm?.accountNumber || ""}
                        onChange={(e) => setField("accountNumber", e.target.value)}
                        placeholder="ACCT-1006 (optional)"
                      />
                    </div>
                    <div>
                      <div className={labelBase}>Phone</div>
                      <input
                        className={inputBase}
                        value={inviteForm?.accountPhone || ""}
                        onChange={(e) => setField("accountPhone", e.target.value)}
                        placeholder="(617) 200-0006"
                      />
                    </div>
                  </div>

                  <div>
                    <div className={labelBase}>Street</div>
                    <input
                      className={inputBase}
                      value={inviteForm?.addressStreet || ""}
                      onChange={(e) => setField("addressStreet", e.target.value)}
                      placeholder="3592 Main St"
                    />
                  </div>

                  <div>
                    <div className={labelBase}>Street 2</div>
                    <input
                      className={inputBase}
                      value={inviteForm?.addressStreet2 || ""}
                      onChange={(e) => setField("addressStreet2", e.target.value)}
                      placeholder="Suite (optional)"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <div className={labelBase}>City</div>
                      <input
                        className={inputBase}
                        value={inviteForm?.addressCity || ""}
                        onChange={(e) => setField("addressCity", e.target.value)}
                        placeholder="Boston"
                      />
                    </div>
                    <div>
                      <div className={labelBase}>State</div>
                      <input
                        className={inputBase}
                        value={inviteForm?.addressState || ""}
                        onChange={(e) => setField("addressState", e.target.value)}
                        placeholder="MA"
                      />
                    </div>
                    <div>
                      <div className={labelBase}>Zip</div>
                      <input
                        className={inputBase}
                        value={inviteForm?.addressZip || ""}
                        onChange={(e) => setField("addressZip", e.target.value)}
                        placeholder="02108"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Contact */}
            <div className="space-y-2 mb-3">
              {isExistingAccountSelected ? (
                <div className="rounded-xl bg-slate-50/90 px-3 py-3">
                  <div className="grid gap-2">
                    <div>
                      <div className={labelBase}>Contact name</div>
                      <div className="mt-1 text-sm font-medium text-slate-900">
                        {inviteForm?.contactName || "No contact name saved"}
                      </div>
                    </div>

                    <div>
                      <div className={labelBase}>Email</div>
                      <div className="mt-1 text-sm text-slate-700 break-all">
                        {inviteForm?.email || "No email saved"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div className={labelBase}>
                      Contact name <span className="text-red-500">*</span>
                    </div>
                    <input
                      className={inputBase}
                      value={inviteForm?.contactName || ""}
                      onChange={(e) => setField("contactName", e.target.value)}
                      placeholder="Yarden Smash"
                    />
                  </div>

                  <div>
                    <div className={labelBase}>
                      Email <span className="text-red-500">*</span>
                    </div>
                    <input
                      className={inputBase}
                      value={inviteForm?.email || ""}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="orders@bostondocksidegril.com"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Existing customer user / pending invite detection */}
            {isCustomerInvite ? (
              <div className="mb-3">
                {checkingCustomerEmail ? (
                  <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 inline-flex items-center gap-2 w-full">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking portal access…
                  </div>
                ) : existingCustomerUserByEmail ? (
                  <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-semibold">Already has portal access</div>
                      <div className="text-emerald-900/80 truncate">
                        {existingCustomerUserByEmail.fullName || existingCustomerUserByEmail.email || "Customer"}
                      </div>
                      {existingCustomerUserByEmail.email ? (
                        <div className="text-emerald-900/80 truncate">{existingCustomerUserByEmail.email}</div>
                      ) : null}
                    </div>
                  </div>
                ) : pendingCustomerInviteByEmail ? (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    <div className="flex items-start gap-2">
                      <Clock className="w-4 h-4 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">Invite already pending</div>
                        <div className="truncate">{pendingCustomerInviteByEmail.email}</div>
                        <div className="text-[11px] text-amber-800/80">
                          Sent: {formatDate(pendingCustomerInviteByEmail.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700"
                        onClick={() => onResendInvite?.(pendingCustomerInviteByEmail)}
                        disabled={!!busyInviteId}
                      >
                        <RefreshCw className="w-4 h-4" />
                        Resend
                      </button>
                      <button
                        type="button"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-amber-200 text-amber-800 font-semibold hover:bg-amber-50"
                        onClick={() => onCancelInvite?.(pendingCustomerInviteByEmail.id)}
                        disabled={!!busyInviteId}
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}

                {customerEmailBlockedReason ? (
                  <div className="mt-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-2 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    <div>{customerEmailBlockedReason}</div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Submit */}
            <button
              type="button"
              onClick={(e) => handleInviteUser?.(e)}
              disabled={!canSubmit}
              className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                !canSubmit
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              title={!canSubmit ? (seatBlockedReason || customerEmailBlockedReason || "Fix errors to send.") : "Send invite"}
            >
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Send invite
            </button>

            {/* Error */}
            {error ? (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                <div className="text-xs text-red-800">{error}</div>
              </div>
            ) : null}

            <div className="mt-3 text-[11px] text-slate-500">
              Tip: If you arrived here from an account, it will auto-select and prefill.
            </div>
          </div>
        </div>

        {/* RIGHT (2/3): All Users List (Updated with grouped dropdowns) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl p-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between mb-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-slate-900 truncate">
                  {isAdminView ? "All Users" : "Customer Portal Access"}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isAdminView
                    ? "Manage all users, invites, and portal access."
                    : "Track the customer invites you sent and the portal users connected to them."}
                </p>
              </div>

            <div className="w-full sm:w-72">
              <input
                className={inputBase}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isAdminView ? "Search all users..." : "Search my customer invites..."}
              />
            </div>
          </div>

          {roleActionError ? (
            <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {roleActionError}
            </div>
          ) : null}

            <div className="space-y-4">
              {userGroups.map((group) => (
                <div key={group.key} className="border border-slate-200 rounded-xl overflow-hidden">
                  {/* Group Header */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {expandedGroups[group.key] ? (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      )}
                      <div className="flex items-center gap-2">
                        {group.icon}
                        <span className="text-sm font-semibold text-slate-900">{group.title}</span>
                      </div>
                      {group.showCount && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${group.color}`}>
                          {group.items.length}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Group Items */}
                  {expandedGroups[group.key] && (
                    <div className="divide-y divide-slate-100">
                      {group.items.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500">
                          {group.emptyText}
                        </div>
                      ) : (
                        group.items.map((item) => (
                          <div key={item.id} className="px-4 py-3 hover:bg-slate-50">
                            {(() => {
                              const stats = getAssignmentStats(item);
                              const showAssignmentStats =
                                group.key === "active" || group.key === "inactive";
                              return (
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="text-sm font-semibold text-slate-900 truncate">
                                    {item.fullName || item.name || item.contactName || "—"}
                                  </div>
                                  {getRoleBadge(item.role, group.key)}
                                </div>
                                
                                <div className="text-xs text-slate-600 truncate mb-1">
                                  {item.email || "—"}
                                </div>
                                
                                <div className="flex flex-wrap gap-2">
                                  {renderPresenceBadge(item, group.key)}

                                  {getCustomerAccountLabel(item) && (
                                    <div className="text-xs text-slate-500 flex items-center gap-1">
                                      <Building className="w-3 h-3" />
                                      {getCustomerAccountLabel(item)}
                                    </div>
                                  )}
                                  
                                  {item.createdAt && (
                                    <div className="text-xs text-slate-500">
                                      {group.key === "pending" ? "Sent: " : "Joined: "}{formatDate(item.createdAt)}
                                    </div>
                                  )}
                                  
                                  {item.lastLoginAt && group.key !== "pending" && (
                                    <div className="text-xs text-slate-500">
                                      Last login: {formatDate(item.lastLoginAt)}
                                    </div>
                                  )}

                                  {showAssignmentStats && (
                                    <>
                            <div className="text-xs text-slate-600 rounded-full bg-slate-100 px-2 py-0.5 whitespace-nowrap">
                              {stats.accountsAssigned} accounts assigned
                            </div>
                            <div className="text-xs text-slate-600 rounded-full bg-slate-100 px-2 py-0.5 whitespace-nowrap">
                              {stats.sampleOrdersAssigned} sample orders
                            </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex flex-col items-end gap-2">
                                {getActionButtons(item, group.key)}
                              </div>
                            </div>
                              );
                            })()}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-3 text-[11px] text-slate-500">
              Customer invites are per-account. If the email already exists as a customer user, you won't be able to invite again.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
